/**
 * CGFinder - Refactored Vehicle Search API Integration
 * Now uses modular architecture with separate classes for different responsibilities
 */
class CGFinder {
  // Constants for configuration
  static CONSTANTS = {
    API_BASE_URL: 'https://dsk889dwqylbo.cloudfront.net/api/v1/cgfinder',
    CACHE_EXPIRY: 365 * 24 * 60 * 60 * 1000, // 1 year
    LOADING_DELAY: 200, // ms
    DEBOUNCE_DELAY: 300, // ms
  };

  static ERROR_MESSAGES = {
    initFailed: 'Failed to initialize vehicle search',
    yearsLoadFailed: 'Failed to load years',
    modelsLoadFailed: 'Failed to load models',
    detailsLoadFailed: 'Failed to load details',
    categoriesLoadFailed: 'Failed to load categories',
    apiError: 'API request failed',
    networkError: 'Network error occurred',
  };

  constructor() {
    // Check if required modules are loaded
    if (!this.checkDependencies()) {
      console.warn('CGFinder modules not loaded yet, deferring initialization...');
      this.deferredInit();
      return;
    }

    // Initialize all managers
    this.apiManager = new window.CGFinderAPI(CGFinder.CONSTANTS.API_BASE_URL);
    this.storageManager = new window.CGFinderStorage();
    this.formManager = new window.CGFinderFormManager();
    this.urlSearchManager = new window.CGFinderUrlSearch();
    this.domUtils = new window.CGFinderDOMUtils();

    // Expose urlSearchManager globally for use in other modules
    window.cgfinderUrlSearch = this.urlSearchManager;

    // Debounced save function
    this.debouncedSave = CGFinder.debounce(
      this.saveCurrentSelections.bind(this),
      CGFinder.CONSTANTS.DEBOUNCE_DELAY
    );

    this.init().catch(error => {
      console.error('CGFinder initialization failed:', error);
      this.domUtils?.showError?.(CGFinder.ERROR_MESSAGES.initFailed);
    });
  }

  /**
   * Check if all required dependencies are loaded
   */
  checkDependencies() {
    const requiredClasses = [
      'CGFinderAPI',
      'CGFinderStorage',
      'CGFinderFormManager',
      'CGFinderUrlSearch',
      'CGFinderDOMUtils'
    ];

    return requiredClasses.every(className =>
      typeof window[className] === 'function'
    );
  }

  /**
   * Defer initialization until dependencies are loaded
   */
  deferredInit() {
    const maxAttempts = 50; // 5 seconds max wait
    let attempts = 0;

    const checkAndInit = () => {
      attempts++;

      if (this.checkDependencies()) {
        // Reinitialize with proper constructor logic
        this.apiManager = new window.CGFinderAPI(CGFinder.CONSTANTS.API_BASE_URL);
        this.storageManager = new window.CGFinderStorage();
        this.formManager = new window.CGFinderFormManager();
        this.urlSearchManager = new window.CGFinderUrlSearch();
        this.domUtils = new window.CGFinderDOMUtils();

        // Expose urlSearchManager globally for use in other modules
        window.cgfinderUrlSearch = this.urlSearchManager;

        this.debouncedSave = CGFinder.debounce(
          this.saveCurrentSelections.bind(this),
          CGFinder.CONSTANTS.DEBOUNCE_DELAY
        );

        this.init().catch(error => {
          console.error('CGFinder initialization failed:', error);
          this.domUtils?.showError?.(CGFinder.ERROR_MESSAGES.initFailed);
        });
      } else if (attempts < maxAttempts) {
        setTimeout(checkAndInit, 100);
      } else {
        console.error('CGFinder dependencies failed to load within timeout');
      }
    };

    setTimeout(checkAndInit, 100);
  }

  /**
   * Initialize CGFinder
   */
  async init() {
    // Inject validation styles
    this.domUtils.injectValidationStyles();

    // Discover and register all forms
    this.formManager.discoverForms();

    // Load initial data
    await this.loadYears();
    await this.loadCategoriesForAllBannerForms();

    // Bind events
    this.bindEvents();

    // Select year from url
    // this.applyFilterYearFromURL();

    // Restore saved selections
    await this.restoreFromLocalStorage();
  }

  /**
   * Load years from API and populate all year dropdowns
   */
  async loadYears() {
    try {
      this.setLoadingState('year', true);
      const yearsData = await this.apiManager.loadYears();
      this.populateAllYearSelects(yearsData);
    } catch (error) {
      this.handleApiError('years', error);
    } finally {
      this.setLoadingState('year', false);
    }
  }

  /**
   * Populate all year select elements
   */
  populateAllYearSelects(yearsData) {
    this.formManager.getAllInstances().forEach((instance) => {
      if (instance.yearSelect) {
        this.domUtils.populateSelectElement(instance.yearSelect, yearsData, {
          valueKey: 'year',
          textKey: 'year',
          placeholder: this.formManager.getPlaceholder('year', instance.variant)
        });
      }
    });
  }

  /**
   * Load models based on selected year for a specific form instance
   */
  async loadModels(yearId, sourceElement = null) {
    const instance = this.formManager.getInstanceByElement(sourceElement);
    if (!instance) return;

    if (!yearId) {
      this.formManager.resetCascadingSelects(instance, ['model', 'details']);
      return;
    }

    const yearText = this.domUtils.getSelectedText(instance.yearSelect);
    if (!yearText) {
      console.warn('Year text not available, cannot load models');
      return;
    }

    try {
      this.domUtils.showLoadingForElement(instance.modelSelect);
      this.domUtils.resetSelectElement(instance.detailsSelect);

      const modelsData = await this.apiManager.loadModels(yearText);

      this.domUtils.populateSelectElement(instance.modelSelect, modelsData, {
        valueKey: 'name',
        textKey: 'name',
        placeholder: this.formManager.getPlaceholder('model', instance.variant)
      });
    } catch (error) {
      this.handleApiError('models', error);
    } finally {
      this.domUtils.hideLoadingForElement(instance.modelSelect);
    }
  }

  /**
   * Load details based on selected model for a specific form instance
   */
  async loadDetails(modelId, sourceElement = null) {
    const instance = this.formManager.getInstanceByElement(sourceElement);
    if (!instance) return;

    if (!modelId) {
      this.domUtils.resetSelectElement(instance.detailsSelect);
      return;
    }

    const yearText = this.domUtils.getSelectedText(instance.yearSelect);
    const modelText = this.domUtils.getSelectedText(instance.modelSelect);

    if (!yearText || !modelText) {
      console.warn('Year or model text not available, cannot load details');
      return;
    }

    try {
      this.domUtils.showLoadingForElement(instance.detailsSelect);

      const detailsData = await this.apiManager.loadDetails(yearText, modelText);


      // If no empty name exists, add a default item with empty name at the beginning
      let processedData = detailsData;


      // Always populate select element as enabled
      const customDetailsData = processedData.map(item => {
        if (!item.name || item.name.trim() === "") {
          return {
            ...item,
            name: "",
            displayText: "Choose Option"
          };
        }
        return {
          ...item,
          displayText: item.name
        };
      });

      this.domUtils.populateSelectElement(instance.detailsSelect, customDetailsData, {
        valueKey: 'name',
        textKey: 'displayText',
        placeholder: this.formManager.getPlaceholder('details', instance.variant),
        enableSelect: true
      });

      // Always default to empty selection first
      let wasAutoSelected = false;
      // Handle auto-selection for single empty items (existing logic)
      wasAutoSelected = this.domUtils.handleAutoSelectDetails(instance, detailsData);

      // If auto-selected, trigger vehicle selection complete event
      if (wasAutoSelected) {
        this.onVehicleSelectionComplete(instance, false);
      }

    } catch (error) {
      this.handleApiError('details', error);
    } finally {
      this.domUtils.hideLoadingForElement(instance.detailsSelect);
    }
  }

  /**
   * Load categories for all banner form instances
   */
  async loadCategoriesForAllBannerForms() {
    const bannerInstances = Array.from(this.formManager.getAllInstances().values()).filter(
      instance => instance.variant === 'banner' && instance.categorySelect
    );

    if (bannerInstances.length === 0) {
      return;
    }

    // Use the first banner instance to load and cache categories
    const firstBannerInstance = bannerInstances[0];
    await this.loadCategories(firstBannerInstance);
  }

  /**
   * Load and cache category options for banner variant
   */
  async loadCategories(instance) {
    if (!instance || !instance.categorySelect) {
      return;
    }

    try {

      // Extract existing options from the select element
      const existingOptions = Array.from(instance.categorySelect.options).slice(1);
      const categoryData = existingOptions.map(option => ({
        value: option.value,
        text: option.textContent.trim()
      }));

      if (categoryData.length > 0) {
        // Apply to all category selects
        this.applyCachedCategoriesToAllForms(categoryData);
      } else {
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }

  /**
   * Apply cached categories to all category select elements
   */
  applyCachedCategoriesToAllForms(categoryData) {
    for (const [formId, instance] of this.formManager.getAllInstances()) {
      if (instance.categorySelect && instance.variant === 'banner') {
        this.domUtils.populateSelectElement(instance.categorySelect, categoryData, {
          valueKey: 'value',
          textKey: 'text',
          placeholder: 'Choose Category'
        });
      }
    }
  }

  /**
   * Bind event listeners to dropdowns
   */
  bindEvents() {
    // Year selection change
    document.addEventListener('change', (e) => {
      if (e.target.matches('.cgfinder-year-select')) {
        const yearId = e.target.value;
        const instance = this.formManager.getInstanceByElement(e.target);

        // Clear validation error when user selects
        if (yearId) {
          this.domUtils.clearValidationError(e.target);
        }

        if (instance) {
          // Clear model and details selections immediately
          if (instance.modelSelect) {
            instance.modelSelect.value = '';
          }
          if (instance.detailsSelect) {
            instance.detailsSelect.value = '';
          }

          this.domUtils.toggleSelectElement(instance.modelSelect, !!yearId);
          this.domUtils.toggleSelectElement(instance.detailsSelect, false);

          // Update save button state after clearing model/details
          this.updateSaveButtonState(instance);
        }

        this.loadModels(yearId, e.target);

        if (instance && instance.variant == "banner" && instance.yearSelect.value !== "" && instance.categorySelect.value !== "" && instance.modelSelect.value === "" && instance.detailsSelect.value === "") {
          this.handleBannerSubmission(instance, {});
        }
      }
    });

    // Model selection change
    document.addEventListener('change', (e) => {
      if (e.target.matches('.cgfinder-model-select')) {
        const modelId = e.target.value;
        const instance = this.formManager.getInstanceByElement(e.target);

        // Clear validation error when user selects
        if (modelId) {
          this.domUtils.clearValidationError(e.target);
        }

        this.loadDetails(modelId, e.target);

        if (instance) {
          this.domUtils.toggleSelectElement(instance.detailsSelect, !!modelId);
          // Update save button state
          this.updateSaveButtonState(instance);
        }
      }
    });

    // Details selection change
    document.addEventListener('change', (e) => {
      if (e.target.matches('.cgfinder-details-select')) {
        const detailsId = e.target.value;
        const instance = this.formManager.getInstanceByElement(e.target);

        if (detailsId && instance) {
          this.onVehicleSelectionComplete(instance, false);
          // Update save button state
          this.updateSaveButtonState(instance);
        }
      }
    });

    // Category selection change
    document.addEventListener('change', (e) => {
      if (e.target.matches('.cgfinder-category-select')) {
        const categoryValue = e.target.value;
        const instance = this.formManager.getInstanceByElement(e.target);

        // Clear validation error when user selects
        if (categoryValue) {
          this.domUtils.clearValidationError(e.target);
        }

        if (categoryValue && instance) {
          document.dispatchEvent(new CustomEvent('cgfinder:categorySelected', {
            detail: {
              formId: instance.formId,
              variant: instance.variant,
              categoryValue: categoryValue,
              categoryText: e.target.options[e.target.selectedIndex]?.text
            }
          }));
          if (instance.variant == "banner" && instance.yearSelect.value !== "" && instance.categorySelect.value !== "" && instance.modelSelect.value === "" && instance.detailsSelect.value === "") {
            this.handleBannerSubmission(instance, {});
          }
        }
      }
    });

    // Search button click handler (for collection variant)
    document.addEventListener('click', (e) => {
      if (e.target.matches('.easysearch-btn-search') || e.target.closest('.easysearch-btn-search')) {
        const button = e.target.matches('.easysearch-btn-search') ? e.target : e.target.closest('.easysearch-btn-search');
        const instance = this.formManager.getInstanceByElement(button);

        if (instance && instance.variant === 'collection') {
          e.preventDefault();

          // Validate required fields first (year and model required, details optional)
          const validation = this.domUtils.validateRequiredFields(instance);
          if (!validation.isValid) {
            console.warn('Collection search blocked - missing required fields:', validation.missingFields);
            return;
          }

          // Get vehicle data
          const { yearSelect, modelSelect, detailsSelect } = instance;
          let metaobjectId = null;
          let searchField = 'vehicle.list1';
          let detailsText = 'none';

          // Handle details - can be empty
          if (detailsSelect?.value) {
            const selectedDetailsOption = this.domUtils.getSelectedOption(detailsSelect);
            metaobjectId = this.domUtils.getAttribute(selectedDetailsOption, 'data-filter-data');
            searchField = this.domUtils.getAttribute(selectedDetailsOption, 'data-search-field') || 'vehicle.list1';
            detailsText = detailsSelect.value;
          } else {
            // Empty details case - use temporary metaobject ID
            metaobjectId = 'gid://shopify/Metaobject/temporary-id';
          }

          // If no metaobject ID from form selection, try to get from current URL
          if (!metaobjectId || metaobjectId === 'gid://shopify/Metaobject/temporary-id') {
            const extractedData = this.urlSearchManager.extractVehicleDataFromUrl();
            if (extractedData.metaobjectId && extractedData.metaobjectId !== 'gid://shopify/Metaobject/temporary-id') {
              metaobjectId = extractedData.metaobjectId;
              searchField = extractedData.searchField || searchField;
            }
          }

          const vehicleData = {
            formId: instance.formId,
            variant: instance.variant,
            yearId: yearSelect?.value,
            yearText: yearSelect?.value,
            modelId: modelSelect?.value,
            modelText: modelSelect?.value,
            detailsId: detailsSelect?.value || '',
            detailsText: detailsText,
            metaobjectId: metaobjectId,
            searchField: searchField
          };

          this.saveCurrentSelections(instance);

          const searchUrl = this.urlSearchManager.buildCollectionSearchUrl(vehicleData, metaobjectId, searchField);
          const facetForm = document.querySelector('#FacetFiltersForm');

          if (facetForm && metaobjectId && metaobjectId !== 'gid://shopify/Metaobject/temporary-id') {
            const currentParams = new URLSearchParams(window.location.search);
            currentParams.set(`filter.p.m.${searchField}`, metaobjectId);

            const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
            window.history.pushState({}, '', newUrl);
            facetForm.dispatchEvent(new Event('input', { bubbles: true }));
          } else {
            this.urlSearchManager.navigateTo(searchUrl);
          }
        }
      }
    });

    // Clear button click handler
    document.addEventListener('click', (e) => {
      if (e.target.matches('.easysearch-btn-clean')) {
        e.preventDefault();
        const instance = this.formManager.getInstanceByElement(e.target);

        if (instance) {
          // Clear search input fields
          this.clearSearchInput();

          // Clear all form instances (not just the clicked one)
          this.formManager.getAllInstances().forEach((formInstance) => {
            this.formManager.resetFormSelections(formInstance);
            this.domUtils.clearAllValidationErrors(formInstance);
          });
          this.clearFormLocalStorage();

          // Update modal headers to reflect cleared state FIRST
          // This sets button text to "SAVE" or "CHANGE VEHICLE" based on stored selections
          if (window.cgFinderModalManager) {
            const allModals = document.querySelectorAll('.vehicle-selector-modal, .product-vehicle-selector-modal');
            allModals.forEach(modal => {
              window.cgFinderModalManager.updateModalHeader(modal);
            });
          }

          // THEN update save button state for each cleared form
          // This will disable the button if in "SAVE" mode, or keep enabled if "CHANGE VEHICLE"
          this.formManager.getAllInstances().forEach((formInstance) => {
            this.updateSaveButtonState(formInstance);
          });

          // If on collection page, close modal and navigate to clean URL (remove vehicle filters)
          if (window.CollectionVehicleURL) {
            const urlManager = new window.CollectionVehicleURL();

            if (urlManager.isSearchPage()) {
              // For search pages, redirect to homepage AND open modal
              if (typeof window !== 'undefined') {
                window.location.href = '/?open_vehicle_modal=true';
              }
            } else {
              // For collection pages, use CollectionVehicleDisplay logic to clear and reload
              // This mimics collection-vehicle-results__clear behavior
              try {
                // Set flag to open modal after page reload
                localStorage.setItem('collection-vehicle-open-modal-after-clear', 'true');
              } catch (error) {
                console.warn('Failed to set modal open flag:', error);
              }

              if (urlManager.isVehicleFilterPage()) {
                if (window.cgFinderModalManager) {
                  // Close all modals before redirecting
                  const allModals = document.querySelectorAll('.vehicle-selector-modal, .product-vehicle-selector-modal');
                  allModals.forEach(modal => {
                    window.cgFinderModalManager.closeModal(modal);
                  });
                }

                // Navigate to clean URL
                urlManager.navigateToCleanUrl();
              }
            }
          }
        }
      }
    });

    // Note: Save button (.vehicle-selector-modal__save) click is handled by
    // cgfinder-modal-manager.js setupCommonModalBehavior() to avoid duplicate execution
  }

  /**
   * Handle vehicle management integration
   */
  handleVehicleManagementIntegration(saveButton, instance) {
    if (instance.yearSelect?.value && instance.modelSelect?.value) {

      const vehicleData = {
        year: parseInt(instance.yearSelect?.value),
        model: instance.modelSelect?.value,
        details: instance.detailsSelect?.value,
        metaobjectId: this.domUtils.getAttribute(
          this.domUtils.getSelectedOption(instance.detailsSelect),
          'data-filter-data'
        ),
        selected: true
      };

      // Find the modal element to get header text
      const modal = saveButton.closest('.vehicle-selector-modal') ||
        saveButton.closest('[class*="modal"]') ||
        document.querySelector('.vehicle-selector-modal');
      const headerElement = modal?.querySelector('.vehicle-selector-modal__header > span');

      // Check if this is a vehicle update based on button text or header text
      const isUpdate = saveButton.textContent.trim() === 'CHANGE VEHICLE' ||
        headerElement?.textContent.trim() === 'UPDATE YOUR VEHICLE';

      if (isUpdate) {
        // Update existing vehicle
        if (window.vehicleManagement && typeof window.vehicleManagement.updateVehicleInfo === 'function') {
          // Get currently selected vehicle's metaobject ID
          const currentSelectedVehicle = this.getCurrentSelectedVehicleMetaobjectId();

          if (currentSelectedVehicle) {
            window.vehicleManagement.updateVehicleInfo(currentSelectedVehicle, vehicleData).then(result => {
              if (result) {
              } else {
                console.warn('Failed to update vehicle info');
              }
            }).catch(error => {
              console.error('Error updating vehicle info:', error);
            });
          } else {
            // No current vehicle found, add as new
            this.addNewVehicle(vehicleData);
          }
        }
      } else {
        // Add new vehicle to customer list
        this.addNewVehicle(vehicleData);
      }
    }
  }

  /**
   * Get currently selected vehicle's metaobject ID from sessionStorage (for updates) or localStorage/DOM
   * @returns {string|null} Current selected vehicle metaobject ID
   */
  getCurrentSelectedVehicleMetaobjectId() {
    try {
      // First check if we're in update mode - get the stored vehicle ID from sessionStorage
      const updatingVehicleId = sessionStorage.getItem('cgfinder-updating-vehicle-id');
      if (updatingVehicleId) {
        // Clear it after reading so it doesn't affect future operations
        sessionStorage.removeItem('cgfinder-updating-vehicle-id');
        return updatingVehicleId;
      }

      // Not in update mode, try to get from localStorage
      const cgfinderData = JSON.parse(localStorage.getItem('cgfinder-selections') || '{}');
      const storedMetaobjectId = cgfinderData['cgfinder-metaobjectId'];

      if (storedMetaobjectId) {
        return storedMetaobjectId;
      }

      // Fallback: get from DOM (selected vehicle item)
      const selectedVehicleItem = document.querySelector('.saved-vehicle-item--selected');
      if (selectedVehicleItem) {
        return selectedVehicleItem.dataset.vehicleMetaobjectId;
      }

      return null;
    } catch (error) {
      console.warn('Error getting current selected vehicle metaobject ID:', error);
      return null;
    }
  }

  /**
   * Add new vehicle to customer list
   * @param {Object} vehicleData - Vehicle data object
   */
  addNewVehicle(vehicleData) {
    if (window.vehicleManagement && typeof window.vehicleManagement.addVehicleToList === 'function') {
      window.vehicleManagement.addVehicleToList(vehicleData).then(result => {
        if (result) {

          // Show vehicle management sections after adding vehicle
          if (typeof window.vehicleManagement.showVehicleManagementSections === 'function') {
            window.vehicleManagement.showVehicleManagementSections();
          }

          // Update button states to reflect the new vehicle
          if (typeof window.vehicleManagement.updateSavedVehicleButtonStates === 'function') {
            window.vehicleManagement.updateSavedVehicleButtonStates();
          }

          // On collection/search pages: close modal and reload with vehicle filter
          if (window.cgFinderModalManager) {
            const pageType = window.cgFinderModalManager.isCollectionOrSearchPage();
            if (pageType.isEither) {
              // Close all open modals first
              const allModals = document.querySelectorAll('.vehicle-selector-modal, .product-vehicle-selector-modal');
              allModals.forEach(modal => window.cgFinderModalManager.closeModal(modal));

              const newUrl = window.cgFinderModalManager.rebuildCurrentUrlWithNewVehicle();
              if (newUrl) {
                window.location.href = newUrl;
              } else {
                window.location.reload();
              }
            }
          }
        } else {
          console.warn('Failed to add vehicle to customer list');
        }
      }).catch(error => {
        console.error('Error adding vehicle to customer list:', error);
      });
    }
  }

  /**
   * Handle vehicle selection completion
   */
  onVehicleSelectionComplete(instance, shouldSave = false) {
    if (!instance) return;

    const { yearSelect, modelSelect, detailsSelect } = instance;

    if (yearSelect?.value && modelSelect?.value && detailsSelect?.value) {
      // Get metaobject ID and search field from selected details option
      const selectedDetailsOption = this.domUtils.getSelectedOption(detailsSelect);
      const metaobjectId = this.domUtils.getAttribute(selectedDetailsOption, 'data-filter-data');
      const searchField = this.domUtils.getAttribute(selectedDetailsOption, 'data-search-field');

      const vehicleData = {
        formId: instance.formId,
        variant: instance.variant,
        yearId: yearSelect.value,
        yearText: yearSelect.value,
        modelId: modelSelect.value,
        modelText: modelSelect.value,
        detailsId: detailsSelect.value,
        detailsText: detailsSelect.value,
        metaobjectId: metaobjectId,
        searchField: searchField
      };

      // Only update easysearch and dispatch events if this is a save operation
      if (shouldSave) {
        this.handleSavedVehicleSelection(vehicleData);
      } else {
        // Dispatch a different event for temporary selection (not saved)
        document.dispatchEvent(new CustomEvent('cgfinder:vehicleTemporarySelected', {
          detail: vehicleData
        }));
      }

      // Handle variant-specific submission logic
      this.handleVariantSubmission(instance, vehicleData);
    }
  }

  /**
   * Handle saved vehicle selection
   */
  handleSavedVehicleSelection(vehicleData) {
    // Build filter tags and index for easysearch compatibility
    const filterTag = `${vehicleData.yearText}-${vehicleData.modelText}-${vehicleData.detailsText}`
      .toLowerCase().replace(/\s+/g, '-');
    const indexData = `${vehicleData.yearText},${vehicleData.modelText},${vehicleData.detailsText}`;

    // Update easysearch localStorage
    this.storageManager.updateEasysearchData(filterTag, indexData, vehicleData);

    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('cgfinder:vehicleSelected', {
      detail: vehicleData
    }));

    // IMPORTANT: Do NOT add to vehicle management here if this is from a modal save button
    // The handleVehicleManagementIntegration function will handle modal saves (add/update)
    // This prevents duplicate API syncs

    // Only add to vehicle management for non-modal contexts (like inline forms, direct selection, etc.)
    const isModalContext = vehicleData.formId && vehicleData.formId.includes('modal');

    if (!isModalContext && window.vehicleManagement && typeof window.vehicleManagement.addVehicleToList === 'function') {
      const newVehicleData = {
        year: vehicleData.yearText,
        model: vehicleData.modelText,
        details: vehicleData.detailsText,
        metaobjectId: vehicleData.metaobjectId,
        selected: true
      };

      window.vehicleManagement.addVehicleToList(newVehicleData)
        .then(result => {
          if (result) {
          }
        })
        .catch(error => {
          console.error('Error adding vehicle to customer list after selection completion:', error);
        });
    } else if (isModalContext) {
    }
  }

  /**
   * Handle variant-specific submission logic
   */
  handleVariantSubmission(instance, vehicleData) {
    if (instance.variant === 'banner') {
      this.handleBannerSubmission(instance, vehicleData);
    } else if (instance.variant === 'collection') {
      this.handleCollectionSubmission(instance, vehicleData);
    }
  }

  /**
   * Handle banner form submission
   */
  handleBannerSubmission(instance, vehicleData) {
    const searchButton = instance.form.querySelector('.easysearch-btn-search');

    if (searchButton && instance.isFormElement) {
      // Add hidden inputs with vehicle data to the form before submission
      this.formManager.addVehicleDataToForm(instance.form, vehicleData);

      const handleSubmit = (e) => {
        e.preventDefault();

        const categoryValue = instance.categorySelect?.value || null;
        if (Object.keys(vehicleData).length === 0 && categoryValue !== "") {
          const searchUrl = this.urlSearchManager.buildSearchUrl({ yearOnly: instance.yearSelect.value }, categoryValue);
          this.urlSearchManager.navigateTo(searchUrl);
        } else {
          // Validate required fields first
          const validation = this.domUtils.validateRequiredFields(instance);
          if (!validation.isValid) {
            console.warn('Banner search blocked - missing required fields:', validation.missingFields);
            return;
          }

          this.saveCurrentSelections(instance);

          const selectedDetailsOption = this.domUtils.getSelectedOption(instance.detailsSelect);
          const metaobjectId = this.domUtils.getAttribute(selectedDetailsOption, 'data-filter-data');
          const searchField = this.domUtils.getAttribute(selectedDetailsOption, 'data-search-field');

          const searchUrl = this.urlSearchManager.buildSearchUrl(vehicleData, categoryValue, metaobjectId, searchField);
          this.urlSearchManager.navigateTo(searchUrl);
        }
      };

      searchButton.removeEventListener('click', handleSubmit);
      searchButton.addEventListener('click', handleSubmit);

    }
  }

  /**
   * Handle collection form submission
   */
  handleCollectionSubmission(instance, vehicleData) {
    const searchButton = instance.form.querySelector('.easysearch-btn-search');

    if (searchButton) {
      const handleSubmit = (e) => {
        e.preventDefault();

        // Validate required fields first  
        const validation = this.domUtils.validateRequiredFields(instance);
        if (!validation.isValid) {
          console.warn('Collection search blocked - missing required fields:', validation.missingFields);
          return;
        }

        this.saveCurrentSelections(instance);

        const selectedDetailsOption = this.domUtils.getSelectedOption(instance.detailsSelect);
        let metaobjectId = this.domUtils.getAttribute(selectedDetailsOption, 'data-filter-data');
        let searchField = this.domUtils.getAttribute(selectedDetailsOption, 'data-search-field') || 'vehicle.list1';

        // If no metaobject ID from form selection, try to get from current URL
        if (!metaobjectId) {
          const extractedData = this.urlSearchManager.extractVehicleDataFromUrl();
          if (extractedData.metaobjectId) {
            metaobjectId = extractedData.metaobjectId;
            searchField = extractedData.searchField || searchField;
          }
        }

        const searchUrl = this.urlSearchManager.buildCollectionSearchUrl(vehicleData, metaobjectId, searchField);
        const facetForm = document.querySelector('#FacetFiltersForm');

        if (facetForm) {
          const currentParams = new URLSearchParams(window.location.search);
          currentParams.set(`filter.p.m.${searchField}`, metaobjectId);

          const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
          window.history.pushState({}, '', newUrl);
          facetForm.dispatchEvent(new Event('input', { bubbles: true }));
        } else {
          this.urlSearchManager.navigateTo(searchUrl);
        }
      };

      searchButton.removeEventListener('click', handleSubmit);
      searchButton.addEventListener('click', handleSubmit);

    }
  }

  /**
   * Save current selections to localStorage
   */
  saveCurrentSelections(instance) {
    if (!instance) return;

    const { yearSelect, modelSelect, detailsSelect, categorySelect } = instance;

    // Build complete vehicle data
    const vehicleData = {
      yearText: yearSelect?.value,
      modelText: modelSelect?.value,
      detailsText: detailsSelect?.value,
      categoryValue: categorySelect?.value,
      metaobjectId: null,
      searchField: null
    };

    // Get metaobject data from selected option
    if (detailsSelect?.value) {
      const selectedOption = this.domUtils.getSelectedOption(detailsSelect);
      vehicleData.metaobjectId = this.domUtils.getAttribute(selectedOption, 'data-filter-data');
      vehicleData.searchField = this.domUtils.getAttribute(selectedOption, 'data-search-field');
    } else {
      vehicleData.detailsText = 'none';
      vehicleData.metaobjectId = 'gid://shopify/Metaobject/temporary-id';
      vehicleData.searchField = null;
    }

    // Save to storage
    this.storageManager.saveVehicleSelection(vehicleData);

    // Trigger vehicle selection complete with save=true
    if (yearSelect?.value && modelSelect?.value && detailsSelect?.value) {
      this.onVehicleSelectionComplete(instance, true);
    }

    // Apply saved selections to all other form instances
    this.applySavedSelectionsToAllForms();

    // Update header labels
    this.domUtils.updateHeaderLabels();
  }

  /**
   * Apply currently saved selections to all form instances
   */
  async applySavedSelectionsToAllForms() {
    try {
      const savedData = this.storageManager.getStoredSelections();

      if (savedData['cgfinder-year-select']) {

        for (const [formId, instance] of this.formManager.getAllInstances()) {
          const currentYear = instance.yearSelect?.value;
          const currentModel = instance.modelSelect?.value;
          const currentDetails = instance.detailsSelect?.value;

          const savedYear = savedData['cgfinder-year-select'];
          const savedModel = savedData['cgfinder-model-select'];
          const savedDetails = savedData['cgfinder-details-select'];

          // Only update if the form doesn't already have these exact selections
          if (currentYear !== savedYear || currentModel !== savedModel || currentDetails !== savedDetails) {
            await this.restoreFormInstanceWithUnifiedData(instance, savedData);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to apply saved selections to all forms:', error);
    }
  }

  /**
   * Restore selections from localStorage
   */
  async restoreFromLocalStorage() {
    try {
      const cgfinderData = this.storageManager.getStoredSelections();

      if (Object.keys(cgfinderData).length === 0) {
        return;
      }

      // Check if we have the unified structure
      const hasUnifiedStructure = cgfinderData['cgfinder-year-select'] ||
        cgfinderData['cgfinder-model-select'] ||
        cgfinderData['cgfinder-details-select'] ||
        cgfinderData['cgfinder-category-select'];

      if (hasUnifiedStructure) {

        let restoredCount = 0;
        for (const [formId, instance] of this.formManager.getAllInstances()) {
          const restored = await this.restoreFormInstanceWithUnifiedData(instance, cgfinderData);
          if (restored) restoredCount++;
        }


        // Update header labels after restoration
        if (restoredCount > 0) {
          this.domUtils.updateHeaderLabels();
        }
      } else {
        await this.restoreLegacyFormats(cgfinderData);
      }

    } catch (error) {
      console.warn('Failed to restore from localStorage:', error);
    }
  }

  /**
   * Restore form instance with unified data
   */
  async restoreFormInstanceWithUnifiedData(instance, cgfinderData) {
    const formId = instance.formId;

    const yearValue = cgfinderData['cgfinder-year-select'];
    const modelValue = cgfinderData['cgfinder-model-select'];
    const detailsValue = cgfinderData['cgfinder-details-select'];
    const categoryValue = cgfinderData['cgfinder-category-select'];

    const hasData = yearValue || modelValue || detailsValue || categoryValue;
    if (!hasData) {
      return false;
    }

    return await this.performRestore(instance, yearValue, modelValue, detailsValue, categoryValue);
  }

  /**
   * Perform restoration of form instance
   */
  async performRestore(instance, yearValue, modelValue, detailsValue, categoryValue) {
    const { formId } = instance;

    try {
      // Restore category selection first
      if (categoryValue && instance.categorySelect) {
        instance.categorySelect.value = categoryValue;
        this.domUtils.toggleSelectElement(instance.categorySelect, true);
      }

      // Restore year selection
      if (yearValue && instance.yearSelect) {
        instance.yearSelect.value = yearValue;
        this.domUtils.toggleSelectElement(instance.yearSelect, true);

        // Load models and restore model selection
        if (modelValue && instance.modelSelect) {
          await this.loadModels(yearValue, instance.yearSelect);

          await new Promise(resolve => setTimeout(resolve, 200));

          instance.modelSelect.value = modelValue;
          if (instance.modelSelect.value === modelValue) {
            this.domUtils.toggleSelectElement(instance.modelSelect, true);

            // Load details options (always load if model was set, even if no details was saved)
            if (instance.detailsSelect) {
              await this.loadDetails(modelValue, instance.modelSelect);

              await new Promise(resolve => setTimeout(resolve, 200));

              // Restore saved details selection if available
              if (detailsValue && detailsValue !== 'none' && detailsValue !== '') {
                instance.detailsSelect.value = detailsValue;
                if (instance.detailsSelect.value === detailsValue) {
                  this.domUtils.toggleSelectElement(instance.detailsSelect, true);

                  this.onVehicleSelectionComplete(instance);
                } else {
                  console.warn(`❌ Details value "${detailsValue}" not found in options for ${formId}`);
                  this.domUtils.toggleSelectElement(instance.detailsSelect, true);
                }
              } else {
                // No details saved — just enable the dropdown with loaded options
                this.domUtils.toggleSelectElement(instance.detailsSelect, true);
              }
            }
          } else {
            console.warn(`❌ Model value "${modelValue}" not found in options for ${formId}`);
            this.domUtils.resetSelectElement(instance.modelSelect,
              this.formManager.getPlaceholder('model', instance.variant));
            this.domUtils.resetSelectElement(instance.detailsSelect,
              this.formManager.getPlaceholder('details', instance.variant));
          }
        }
      }

      // Update save button state after restoration
      this.updateSaveButtonState(instance);

      return true;
    } catch (error) {
      console.error(`❌ Failed to restore selections for form ${formId}:`, error);
      return false;
    }
  }

  /**
   * Restore legacy formats (backward compatibility)
   */
  async restoreLegacyFormats(/* cgfinderData */) {
    // Implementation would go here if needed
  }

  /**
   * Clear selections for a specific form instance
   */
  clearFormInstance(instance) {
    if (!instance) return;

    this.formManager.clearFormInstance(instance);
    this.clearFormLocalStorage();
  }

  /**
   * Clear localStorage
   */
  clearFormLocalStorage() {
    this.storageManager.clearSelections();
    this.domUtils.updateHeaderLabels();
  }

  /**
   * Clear search input fields (name="q")
   */
  clearSearchInput() {
    try {
      const searchInputs = document.querySelectorAll('input[name="q"]');
      searchInputs.forEach(input => {
        if (input.type !== 'hidden') {
          input.value = '';
        }
      });

      // Remove q parameter from URL if it exists
      const url = new URL(window.location.href);
      if (url.searchParams.has('q')) {
        url.searchParams.delete('q');
        window.history.replaceState({}, '', url.toString());
      }

    } catch (error) {
      console.warn('Error clearing search input:', error);
    }
  }

  /**
   * Set loading state for all instances of a select type
   */
  setLoadingState(selectType, isLoading) {
    this.formManager.getAllInstances().forEach((instance) => {
      const element = instance[`${selectType}Select`];
      if (element) {
        if (isLoading) {
          this.domUtils.showLoadingForElement(element);
        } else {
          this.domUtils.hideLoadingForElement(element);
        }
      }
    });
  }

  /**
   * Handle API errors consistently
   */
  handleApiError(endpoint, error) {
    console.error(`Failed to load ${endpoint}:`, error);
    const errorMessage = CGFinder.ERROR_MESSAGES[`${endpoint}LoadFailed`] || CGFinder.ERROR_MESSAGES.networkError;
    this.domUtils.showError(errorMessage);
  }

  // === VALIDATION METHODS ===

  /**
   * Validate all forms and show errors
   */
  validateAllForms() {
    let allValid = true;
    const results = [];

    this.formManager.getAllInstances().forEach((instance, formId) => {
      const validation = this.domUtils.validateRequiredFields(instance);
      results.push({
        formId: formId,
        variant: instance.variant,
        isValid: validation.isValid,
        missingFields: validation.missingFields
      });

      if (!validation.isValid) {
        allValid = false;
      }
    });

    return { allValid, results };
  }

  /**
   * Validate specific form instance
   */
  validateForm(formId) {
    const instance = this.formManager.getInstance(formId);
    if (!instance) {
      console.warn(`Form instance not found: ${formId}`);
      return { isValid: false, missingFields: [] };
    }

    return this.domUtils.validateRequiredFields(instance);
  }

  /**
   * Update save button state based on year and model selection
   */
  updateSaveButtonState(instance) {
    if (!instance) return;

    // Find the save button in the same form or modal
    const formElement = instance.form || instance.yearSelect?.closest('form') || instance.yearSelect?.closest('.vehicle-selector-modal');
    if (!formElement) return;

    const saveButton = formElement.querySelector('.vehicle-selector-modal__save');
    if (!saveButton) return;

    // Don't disable the button if it's in "CHANGE VEHICLE" mode
    if (saveButton.textContent.trim() === 'CHANGE VEHICLE') {
      saveButton.disabled = false;
      saveButton.classList.remove('vehicle-selector-modal__save-disabled');
      return;
    }

    const hasYear = instance.yearSelect?.value && instance.yearSelect.value.trim() !== '';
    const hasModel = instance.modelSelect?.value && instance.modelSelect.value.trim() !== '';

    if (hasYear && hasModel) {
      saveButton.disabled = false;
      saveButton.classList.remove('vehicle-selector-modal__save-disabled');
    } else {
      saveButton.disabled = true;
      saveButton.classList.add('vehicle-selector-modal__save-disabled');
    }
  }

  // === PUBLIC API METHODS ===

  /**
   * Clear all selections for all forms
   */
  clearSelections() {

    this.formManager.getAllInstances().forEach((instance) => {
      ['year', 'model', 'details', 'category'].forEach(type => {
        const element = instance[`${type}Select`];
        if (element) {
          this.domUtils.resetSelectElement(element, this.formManager.getPlaceholder(type, instance.variant));
        }
      });
    });

    this.storageManager.clearSelections();
    this.domUtils.updateHeaderLabels();
  }

  /**
   * Get current selection
   */
  getCurrentSelection(formId = null) {
    if (formId) {
      const instance = this.formManager.getInstance(formId);
      return instance ? this.formManager.getInstanceSelection(instance) : null;
    }

    // Return first valid selection (year + model required, details optional)
    for (const [, instance] of this.formManager.getAllInstances()) {
      const selection = this.formManager.getInstanceSelection(instance);
      if (selection.year.id && selection.model.id) {
        return selection;
      }
    }

    return null;
  }

  /**
   * Get debug info
   */
  getDebugInfo() {
    return {
      ...this.formManager.getDebugInfo(),
      apiMetrics: this.apiManager.getMetrics(),
      domMetrics: this.domUtils.getMetrics(),
      storedSelections: this.storageManager.getStoredSelections(),
      urlInfo: this.urlSearchManager.getUrlDebugInfo()
    };
  }

  /**
   * Log debug info to console
   */
  logDebugInfo() {
    const info = this.getDebugInfo();
    return info;
  }

  /**
   * Set test data (for testing)
   */
  setTestData(year, model, details, category = null, metaobjectId = null, searchField = null) {
    return this.storageManager.setTestData(year, model, details, category, metaobjectId, searchField);
  }

  /**
   * Manually restore selections
   */
  async restoreSelections() {
    await this.restoreFromLocalStorage();
  }

  // === STATIC UTILITY METHODS ===

  /**
   * Static method to get search string from selections
   */
  static getSearchStringFromSelections() {
    if (typeof CGFinderStorage !== 'undefined') {
      const storage = new CGFinderStorage();
      return storage.getSearchString();
    }
    return null;
  }

  /**
   * Static method to get vehicle data with URL fallback
   */
  static getCGFinderDataWithUrlFallback() {
    if (typeof CGFinderStorage !== 'undefined') {
      const storage = new CGFinderStorage();
      return storage.getComprehensiveVehicleData();
    }
    return { metaobjectId: null, year: null, model: null, details: null, category: null };
  }

  /**
   * Static method to check if has complete vehicle selection
   */
  static hasCompleteVehicleSelection() {
    return CGFinder.getSearchStringFromSelections() !== null;
  }

  /**
   * Static method to get vehicle string for header
   */
  static getVehicleStringForHeader() {
    if (typeof CGFinderStorage !== 'undefined') {
      const storage = new CGFinderStorage();
      return storage.getVehicleStringForHeader();
    }
    return null;
  }

  /**
   * Static method to extract vehicle data from URL
   */
  static extractVehicleDataFromUrl() {
    if (typeof CGFinderUrlSearch !== 'undefined') {
      const urlSearch = new CGFinderUrlSearch();
      return urlSearch.extractVehicleDataFromUrl();
    }
    return { metaobjectId: null, searchField: null, year: null, model: null };
  }

  /**
   * Static debounce utility function
   */
  static debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

/**
 * Lazy initialization - only initialize when forms are present on the page
 * Uses requestIdleCallback for non-blocking initialization
 */
function initializeCGFinderLazy() {
  // Check if CGFinder forms exist on the page
  const hasForms = document.querySelectorAll('.cgfinder-year-select').length > 0;

  // Also check for modal triggers or other indicators
  const hasModalTriggers = document.querySelectorAll('.header__label-search.button--white, .link-add-vehicle').length > 0;

  // Only initialize if forms or triggers are present
  if (!hasForms && !hasModalTriggers) {
    // Defer check - forms might be added dynamically
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => {
        setTimeout(initializeCGFinderLazy, 1000);
      }, { timeout: 2000 });
    } else {
      setTimeout(initializeCGFinderLazy, 1000);
    }
    return;
  }

  // Initialize CGFinder
  if (typeof window !== 'undefined' && !window.CGFinder) {
    try {
      window.CGFinder = new CGFinder();
    } catch (error) {
      console.error('CGFinder initialization error:', error);
    }
  }
}

// Initialize when DOM is ready, but defer non-critical work
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // Use requestIdleCallback for non-blocking initialization
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(initializeCGFinderLazy, { timeout: 2000 });
    } else {
      // Fallback: use setTimeout with small delay
      setTimeout(initializeCGFinderLazy, 100);
    }
  });
} else {
  // DOM already loaded
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(initializeCGFinderLazy, { timeout: 2000 });
  } else {
    setTimeout(initializeCGFinderLazy, 100);
  }
}